import { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Collapse,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { getTeamsTournamentRounds } from '../../services/tournament.service';

const toNum = (v) => (v === null || v === undefined ? null : Number(v));

const fmtNormal = (res) => {
  const r = toNum(res);
  if (r === 1) return '1';
  if (r === 0) return '0';
  if (r === 2) return '½';
  return '!';
};

const getResultDisplay = (p) => {
  const tipoB = toNum(p.par_tipo_res_blancas);
  const tipoN = toNum(p.par_tipo_res_negras);
  const resB = toNum(p.par_resultado_blancas);
  const resN = toNum(p.par_resultado_negras);

  if (tipoB === 3 && tipoN === 3) {
    return { tipo: 'bye', resB: fmtNormal(resB) };
  }
  if (tipoB === 2 && tipoN === 2) {
    return { tipo: 'ausencia', resB: resB === 1 ? '+' : '-', resN: resN === 1 ? '+' : '-' };
  }
  return { tipo: 'normal', resB: fmtNormal(resB), resN: fmtNormal(resN) };
};

const headerSx = {
  background: '#565656',
  color: '#e2e8f0',
  fontWeight: 700,
  letterSpacing: 0.5,
};

const cellSx = {
  background: '#9295a9',
  color: '#1a1f2e',
  fontWeight: 500,
  borderBottom: '1px solid rgba(0,0,0,0.15)',
};

const resultCellSx = {
  ...cellSx,
  textAlign: 'center',
  fontWeight: 700,
  fontFamily: 'monospace',
  fontSize: '0.95rem',
  width: 80,
};

const MatchupRow = ({ p }) => {
  const [open, setOpen] = useState(false);
  const isBye = p.equ_2_id === 0;
  const hasGames = p.partidasJugadas && p.partidasJugadas.length > 0;
  const isExpandable = hasGames && !isBye;

  return (
    <>
      <TableRow 
        hover 
        onClick={() => isExpandable && setOpen(!open)} 
        style={{ cursor: isExpandable ? 'pointer' : 'default' }}
      >
        <TableCell sx={{ ...cellSx, width: 50, textAlign: 'center', color: '#1a1f2e', padding: '4px' }}>
          {isExpandable ? (
            open ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />
          ) : null}
        </TableCell>
        <TableCell sx={cellSx}>{p.equipo_1 ?? '—'}</TableCell>

        {isBye ? (
          <>
            <TableCell
              colSpan={2}
              sx={{ ...resultCellSx, letterSpacing: 2 }}
            >
              {p.puntos_equ_1 ?? '—'}
            </TableCell>
            <TableCell sx={{ ...cellSx, fontStyle: 'italic', color: '#334155' }}>
              BYE
            </TableCell>
          </>
        ) : (
          <>
            <TableCell sx={resultCellSx}>{p.puntos_equ_1 ?? '—'}</TableCell>
            <TableCell sx={resultCellSx}>{p.puntos_equ_2 ?? '—'}</TableCell>
            <TableCell sx={cellSx}>{p.equipo_2 ?? '—'}</TableCell>
          </>
        )}
      </TableRow>

      {hasGames && !isBye && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0, background: '#2c2e38' }} colSpan={5}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2, padding: 2, background: '#1c1f26', border: '1px solid rgba(255,255,255,0.08)' }}>
                <Table size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ background: '#3b3e4a', color: '#e2e8f0', fontWeight: 600, width: 80, borderBottom: 'none', textAlign: 'center' }}>Tablero</TableCell>
                      <TableCell sx={{ background: '#3b3e4a', color: '#e2e8f0', fontWeight: 600, borderBottom: 'none' }}>Blancas</TableCell>
                      <TableCell colSpan={2} sx={{ background: '#3b3e4a', color: '#e2e8f0', fontWeight: 600, borderBottom: 'none', textAlign: 'center', width: 120 }}>Resultado</TableCell>
                      <TableCell sx={{ background: '#3b3e4a', color: '#e2e8f0', fontWeight: 600, borderBottom: 'none' }}>Negras</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {p.partidasJugadas.map((game, gIdx) => {
                      const rd = getResultDisplay(game);

                      return (
                        <TableRow key={game.par_id || gIdx}>
                          <TableCell sx={{ background: '#22252c', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontWeight: 700 }}>
                            {gIdx + 1}
                          </TableCell>
                          <TableCell sx={{ background: '#22252c', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            {game.jugador_blancas}
                          </TableCell>

                          {rd.tipo === 'ausencia' ? (
                            <>
                              <TableCell sx={{ background: '#22252c', color: rd.resB === '+' ? '#15803d' : '#ef4444', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontWeight: 700, fontFamily: 'monospace', width: 60 }}>
                                {rd.resB}
                              </TableCell>
                              <TableCell sx={{ background: '#22252c', color: rd.resN === '+' ? '#15803d' : '#ef4444', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontWeight: 700, fontFamily: 'monospace', width: 60 }}>
                                {rd.resN}
                              </TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell sx={{ background: '#22252c', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontWeight: 700, fontFamily: 'monospace', width: 60 }}>
                                {rd.resB ?? '!'}
                              </TableCell>
                              <TableCell sx={{ background: '#22252c', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.05)', textAlign: 'center', fontWeight: 700, fontFamily: 'monospace', width: 60 }}>
                                {rd.resN ?? '!'}
                              </TableCell>
                            </>
                          )}

                          <TableCell sx={{ background: '#22252c', color: '#e2e8f0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                            {game.jugador_negras}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const TournamentTeamsRounds = ({ torId }) => {
  const [rounds, setRounds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    const fetchRounds = async () => {
      setIsLoading(true);
      try {
        const data = await getTeamsTournamentRounds(torId);

        // Ordenar los enfrentamientos de cada ronda para que los BYE queden al final
        const sortedData = data.map((round) => ({
          ...round,
          partidas: [...round.partidas].sort((a, b) => {
            const isByeA = a.equ_2_id === 0;
            const isByeB = b.equ_2_id === 0;
            if (isByeA && !isByeB) return 1;
            if (!isByeA && isByeB) return -1;
            return 0;
          }),
        }));

        setRounds(sortedData);
      } catch (e) {
        setIsError(true);
      } finally {
        setIsLoading(false);
      }
    };
    fetchRounds();
  }, [torId]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress sx={{ color: '#7c8cf8' }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ py: 3 }}>
        Error al cargar las rondas por equipo.
      </Typography>
    );
  }

  if (!rounds.length) {
    return (
      <Typography sx={{ color: '#9aa5b4', py: 3 }}>
        No hay rondas registradas para este torneo por equipos.
      </Typography>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {rounds.map((round) => (
        <Box key={round.numero}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5, maxWidth: 700, mx: 'auto' }}>
            <Typography
              variant="h6"
              sx={{ color: '#e2e8f0', fontWeight: 700, letterSpacing: 0.5 }}
            >
              Ronda {round.numero}
            </Typography>
            <Divider
              sx={{ flex: 1, borderColor: 'rgba(124,140,248,0.25)' }}
            />
            <Typography variant="caption" sx={{ color: '#9aa5b4' }}>
              {(() => {
                const n = round.partidas.length;
                return `${n} enfrentamiento${n !== 1 ? 's' : ''}`;
              })()}
            </Typography>
          </Box>

          {round.partidas.length === 0 ? (
            <Typography sx={{ color: '#9aa5b4', fontSize: '0.85rem', pl: 1 }}>
              Sin enfrentamientos registrados
            </Typography>
          ) : (
            <Box sx={{ maxWidth: 700, mx: 'auto' }}>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: 0,
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'transparent',
                }}
              >
                <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...headerSx, width: 50, textAlign: 'center' }}></TableCell>
                      <TableCell sx={headerSx}>Equipo 1</TableCell>
                      <TableCell colSpan={2} sx={{ ...headerSx, textAlign: 'center', width: 160 }}>
                        Resultado
                      </TableCell>
                      <TableCell sx={headerSx}>Equipo 2</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {round.partidas.map((p, idx) => (
                      <MatchupRow key={idx} p={p} />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default TournamentTeamsRounds;
